/**
 * YearInReviewPage.js
 *
 * Gallery page displaying year-end reviews
 * Features: infinite scroll, responsive design
 */

import React, { useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import { PageContainer } from '../components/Container/PageContainer';
import { Section } from '../components/Container/Section';
import { YearEndGalleryCard } from '../components/YearEnd/YearEndGalleryCard';
import { useYearEndGallery, useInfiniteScrollObserver } from '../hooks/useYearEndGallery';
import { useIsMobile } from '../hooks/useIsMobile';

const YearInReviewPage = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile('md');

  const {
    items,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    loadInitial,
    loadMore
  } = useYearEndGallery({ year: 2025 });

  // Initial load
  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  // Infinite scroll observer
  const loadMoreRef = useInfiniteScrollObserver({
    onIntersect: loadMore,
    enabled: hasMore && !isLoadingMore && !isLoading
  });

  return (
    <PageContainer>
      <Section>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1 }}>
            <Typography
              variant={isMobile ? 'h4' : 'h3'}
              fontWeight={800}
            >
              {t('yearInReview.title', '2025 Year in Review')}
            </Typography>
            {!isLoading && items.length > 0 && (
              <Typography
                variant="h6"
                fontWeight={600}
                color="primary.main"
              >
                {items.length.toLocaleString()}{t('yearInReview.peopleCount', '명')}
              </Typography>
            )}
          </Box>
          <Typography variant="body1" color="text.secondary">
            {t('yearInReview.subtitle', 'Explore how others spent their year on Threads')}
          </Typography>
        </Box>

        {/* Initial Loading */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="error" gutterBottom>
              {t('yearInReview.error', 'Failed to load. Please try again.')}
            </Typography>
            <Typography
              variant="body2"
              color="primary"
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => loadInitial()}
            >
              {t('common.retry', 'Retry')}
            </Typography>
          </Box>
        )}

        {/* Content Grid */}
        {!isLoading && !error && (
          <Box>
            {/* Card Grid */}
            {items.length > 0 && (
              <Grid container spacing={2}>
                {items.map((item) => (
                  <Grid key={item.id} size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                    <YearEndGalleryCard yearEndData={item} />
                  </Grid>
                ))}
              </Grid>
            )}

            {/* Empty State */}
            {items.length === 0 && (
              <Box sx={{ py: 8, textAlign: 'center' }}>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  gutterBottom
                >
                  {t('yearInReview.empty', 'No year reviews yet')}
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  {t('yearInReview.emptyHint', 'Be the first to share your year!')}
                </Typography>
              </Box>
            )}

            {/* Infinite Scroll Sentinel */}
            {hasMore && (
              <Box
                ref={loadMoreRef}
                sx={{
                  py: 4,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                {isLoadingMore && <CircularProgress size={24} />}
              </Box>
            )}

            {/* End of List */}
            {!hasMore && items.length > 0 && (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.disabled">
                  {t('yearInReview.endOfList', "You've reached the end")}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Section>
    </PageContainer>
  );
};

export default YearInReviewPage;
